/**
 * Assessments list view.
 *
 * Role-aware (T2):
 *  - Super admins load every assessment (``listAssessments``) and rows
 *    open the full editor (``assessment-edit``). They can also create a
 *    new assessment.
 *  - Admins and managers load only the assessments assigned to teams in
 *    their visibility scope (``listAssessmentsScoped``) and rows open
 *    the read-only detail view (``assessment-view``). No create button.
 *
 * Class strings for the section card, table header, row hover, status
 * pill and chip are copied verbatim from TeamsView / the design
 * appendix so the table is visually indistinguishable.
 */
import { useQuery } from "@tanstack/react-query";
import { api, type AssessmentRow, type UserOut } from "../lib/api";
import type { NavKey } from "../lib/router";

export function AssessmentsView({
  me,
  navigate,
}: {
  me: UserOut;
  // `assessment-edit` / `assessment-view` are sub-view nav keys handled
  // by App.tsx; this view forwards clicks to the right one per role.
  navigate: (nav: NavKey, id: string) => void;
}) {
  const isSupadmin = me.role === "supadmin";
  // Super admins still see drafts via the editor list; admins/managers
  // get the scoped read-only list of in-scope assessments. Separate
  // cache keys keep the two datasets from colliding.
  const assessmentsQ = useQuery<AssessmentRow[]>({
    queryKey: isSupadmin ? ["assessments"] : ["assessments", "scoped"],
    queryFn: () =>
      isSupadmin ? api.listAssessments() : api.listAssessmentsScoped(),
  });
  const targetNav: NavKey = isSupadmin ? "assessment-edit" : "assessment-view";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Assessments</h2>
        {isSupadmin && (
          <button
            type="button"
            onClick={() => navigate("assessment-edit", "new")}
            className="rounded bg-primary text-primary-foreground text-sm px-3 py-1.5 hover:bg-primary/90 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
          >
            + New assessment
          </button>
        )}
      </div>

      <section className="rounded-lg border border-border bg-card shadow-md animate-card-enter">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/10 text-muted-foreground text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Assigned teams</th>
              </tr>
            </thead>
            <tbody>
              {(assessmentsQ.data ?? []).map((a) => (
                <tr
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(targetNav, a.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(targetNav, a.id);
                    }
                  }}
                  className="border-t border-border hover:bg-primary/5 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] cursor-pointer"
                >
                  <td className="px-3 py-2 font-medium">{a.name}</td>
                  <td className="px-3 py-2">
                    <AssessmentStatusPill status={a.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {a.teams.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {a.teams.map((t) => (
                          <span
                            key={t.id}
                            className="inline-flex rounded-full bg-muted border border-border px-2 py-0.5 text-xs text-foreground"
                          >
                            {t.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {assessmentsQ.isLoading ? (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : (assessmentsQ.data?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No assessments yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function AssessmentStatusPill({ status }: { status: "draft" | "saved" }) {
  const cls = status === "saved"
    ? "bg-primary/15 text-primary"
    : "bg-accent/20 text-accent-foreground";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}
